import { Panel } from '@elotech/components';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { alert } from '../../components';
import {
  DynamicField,
  FormularioDinamicoRuntime,
  PerguntaInline,
  RespostaFormularioDinamico
} from '../../type/FormularioDinamico';
import { filtrarVisiveis } from '../../utils/FormularioCondicaoUtils';
import { textToHtml } from '../../utils/StringUtils';
import AcoesFormularios from './AcoesFormularios';
import FormularioDinamicoCard from './FormularioDinamicoCard';
import {
  estruturaVazia,
  mesmoConteudo,
  paraPerguntaInline,
  valoresAplicaveis,
  valoresPorInstancia
} from './formularioValores';
import { situacaoDe } from './situacao';
import { useArquivoHandlers } from './useArquivoHandlers';

interface Props {
  formularios: FormularioDinamicoRuntime[];
  contexto: Record<string, unknown>;
  somenteLeitura?: boolean;
  nomeUsuarioLogado?: string;
  tokenPromise?: () => Promise<string>;
  fileProcessor?: (
    instanciaId: string,
    fileObject: any,
    file: File
  ) => Promise<string>;
  fileDownloader?: (instanciaId: string, fileObject: any) => Promise<boolean>;
  onSalvar: (respostas: RespostaFormularioDinamico[]) => Promise<unknown>;
  onLimpar?: (instanciaIds: string[]) => Promise<unknown>;
  onSincronizarPerguntas?: (
    instanciaId: string,
    perguntas: PerguntaInline[]
  ) => Promise<unknown>;
}

export interface FormularioDinamicoPanelHandle {
  salvarAlteracoes: () => Promise<unknown>;
}

export const FormularioDinamicoPanel = forwardRef<
  FormularioDinamicoPanelHandle,
  Props
>(function FormularioDinamicoPanel(
  {
    formularios,
    contexto,
    somenteLeitura,
    nomeUsuarioLogado,
    tokenPromise,
    fileProcessor,
    fileDownloader,
    onSalvar,
    onLimpar,
    onSincronizarPerguntas
  },
  ref
) {
  const [valores, setValores] = useState<
    Record<string, Record<string, unknown>>
  >(() => valoresPorInstancia(formularios));

  const [valoresAplicados, setValoresAplicados] = useState<
    Record<string, Record<string, unknown>>
  >(() => valoresAplicaveis(formularios, nomeUsuarioLogado));

  const [persistidos, setPersistidos] = useState<
    Record<string, Record<string, unknown>>
  >(() => valoresPorInstancia(formularios));

  const primeiraRenderizacao = useRef(true);

  useEffect(() => {
    if (primeiraRenderizacao.current) {
      primeiraRenderizacao.current = false;
      return;
    }

    const atuais = valoresPorInstancia(formularios);

    setValoresAplicados(valoresAplicaveis(formularios, nomeUsuarioLogado));
    setPersistidos(atuais);
    setValores(atuais);
  }, [formularios, nomeUsuarioLogado]);

  const [editandoPerguntas, setEditandoPerguntas] = useState<
    string | undefined
  >();

  const [rascunhoPerguntas, setRascunhoPerguntas] = useState<
    DynamicField[] | undefined
  >();

  const [loadingPerguntas, setLoadingPerguntas] = useState(false);

  const [limpando, setLimpando] = useState(false);

  const [salvando, setSalvando] = useState(false);

  const [valido, setValido] = useState<Record<string, boolean>>({});

  const arquivoDe = useArquivoHandlers(
    formularios,
    fileProcessor,
    fileDownloader
  );

  const onStatusChanges = useCallback(
    (instanciaId: string, valida: boolean) =>
      setValido(atual => ({ ...atual, [instanciaId]: valida })),
    []
  );

  const onValueChanges = useCallback(
    (instanciaId: string, detail: any) =>
      setValores(atual => ({ ...atual, [instanciaId]: detail })),
    []
  );

  const perguntasCustomizadas = useMemo(
    () =>
      formularios.reduce<Record<string, DynamicField[]>>(
        (acumulado, formulario) => ({
          ...acumulado,
          [formulario.id]: (formulario.fields ?? []).filter(
            campo => campo?.origem === 'INLINE'
          )
        }),
        {}
      ),
    [formularios]
  );

  const alternarEdicaoPerguntas = (instanciaId: string) => {
    setRascunhoPerguntas(undefined);
    setEditandoPerguntas(
      editandoPerguntas === instanciaId ? undefined : instanciaId
    );
  };

  const confirmarPerguntas = (instanciaId: string) => {
    if (!onSincronizarPerguntas || !rascunhoPerguntas) {
      return;
    }

    const perguntas = rascunhoPerguntas
      .filter(campo => !!campo?.label && !!campo?.type)
      .map(paraPerguntaInline);

    setLoadingPerguntas(true);

    Promise.resolve(onSincronizarPerguntas(instanciaId, perguntas))
      .then(() => {
        setRascunhoPerguntas(undefined);
        setEditandoPerguntas(undefined);
      })
      .finally(() => setLoadingPerguntas(false));
  };

  const visiveis = useMemo(
    () =>
      filtrarVisiveis(
        formularios.map(formulario => ({
          ...formulario,
          values: valores[formulario.id] ?? formulario.values
        })),
        contexto
      ),
    [formularios, valores, contexto]
  );

  const salvarTodos = () => {
    if (salvando) {
      return Promise.resolve();
    }

    setSalvando(true);

    return executarSalvamento().finally(() => setSalvando(false));
  };

  const alterados = () =>
    visiveis.filter(
      formulario =>
        !mesmoConteudo(
          valores[formulario.id] ?? persistidos[formulario.id] ?? {},
          persistidos[formulario.id] ?? {}
        )
    );

  const salvarAlteracoes = () => {
    const formulariosAlterados = alterados();

    if (somenteLeitura || salvando || formulariosAlterados.length === 0) {
      return Promise.resolve();
    }

    setSalvando(true);

    return executarSalvamento(formulariosAlterados).finally(() =>
      setSalvando(false)
    );
  };

  useImperativeHandle(ref, () => ({ salvarAlteracoes }));

  const executarSalvamento = (
    formulariosParaSalvar: FormularioDinamicoRuntime[] = visiveis
  ) => {
    const invalidosParaSalvar = formulariosParaSalvar.filter(
      formulario => valido[formulario.id] === false
    );

    const validos = formulariosParaSalvar.filter(
      formulario => valido[formulario.id] !== false
    );

    const salvamento =
      validos.length > 0
        ? Promise.resolve(
            onSalvar(
              validos.map(formulario => ({
                instanciaId: formulario.id,
                values: valores[formulario.id] ?? formulario.values ?? {}
              }))
            )
          )
        : Promise.resolve();

    if (invalidosParaSalvar.length === 0) {
      return salvamento;
    }

    const titulosDe = (lista: FormularioDinamicoRuntime[]) =>
      lista.map(formulario => formulario.titulo).join(', ');

    const mensagem = [
      validos.length > 0 ? `Salvos: ${titulosDe(validos)}.` : '',
      `Com pendências: ${titulosDe(invalidosParaSalvar)}.`,
      'Revise o preenchimento dos formulários com pendências.'
    ]
      .filter(Boolean)
      .join('\n');

    return salvamento.then(() =>
      alert({
        type: 'warning',
        title:
          invalidosParaSalvar.length === 1
            ? 'Formulário com pendências'
            : 'Formulários com pendências',
        html: textToHtml(mensagem)
      })
    );
  };

  const aplicarLimpezaNaTela = () => {
    setValoresAplicados(
      visiveis.reduce<Record<string, Record<string, unknown>>>(
        (acumulado, formulario) => ({
          ...acumulado,
          [formulario.id]: estruturaVazia(formulario.fields)
        }),
        {}
      )
    );

    setValores(
      visiveis.reduce<Record<string, Record<string, unknown>>>(
        (acumulado, formulario) => ({ ...acumulado, [formulario.id]: {} }),
        {}
      )
    );
  };

  const limpar = () =>
    alert({
      type: 'warning',
      title: 'Limpar formulários',
      text:
        'Todas as respostas já gravadas nos formulários dinâmicos serão excluídas. ' +
        'Esta ação não pode ser desfeita. Deseja continuar?',
      showCancelButton: true,
      confirmButtonText: 'Limpar',
      onConfirm: () => {
        if (!onLimpar) {
          return;
        }

        setLimpando(true);

        Promise.resolve(onLimpar(visiveis.map(formulario => formulario.id)))
          .then(aplicarLimpezaNaTela)
          .finally(() => setLimpando(false));
      }
    });

  if (visiveis.length === 0) {
    return (
      <Panel isTable={false} title="Formulários">
        <p className="mt-xs">
          Nenhum formulário dinâmico disponível para este registro.
        </p>
      </Panel>
    );
  }

  return (
    <>
      {visiveis.map(formulario => (
        <FormularioDinamicoCard
          key={formulario.id}
          formulario={formulario}
          situacao={situacaoDe(
            formulario,
            valores,
            persistidos,
            valido[formulario.id] === false
          )}
          values={valores[formulario.id] ?? formulario.values}
          valuesAplicados={valoresAplicados[formulario.id]}
          somenteLeitura={somenteLeitura}
          tokenPromise={tokenPromise}
          arquivo={arquivoDe[formulario.id]}
          perguntasCustomizadas={perguntasCustomizadas[formulario.id]}
          editandoPerguntas={editandoPerguntas === formulario.id}
          salvandoPerguntas={loadingPerguntas}
          temRascunhoPerguntas={!!rascunhoPerguntas}
          permiteEditarPerguntas={!!onSincronizarPerguntas}
          onValueChanges={detail => onValueChanges(formulario.id, detail)}
          onStatusChanges={valida => onStatusChanges(formulario.id, valida)}
          onAlternarEdicaoPerguntas={() =>
            alternarEdicaoPerguntas(formulario.id)
          }
          onRascunhoPerguntasChange={fields => setRascunhoPerguntas(fields)}
          onConfirmarPerguntas={() => confirmarPerguntas(formulario.id)}
        />
      ))}

      {!somenteLeitura && (
        <AcoesFormularios
          desabilitado={limpando || salvando || !!editandoPerguntas}
          onSalvar={salvarTodos}
          onLimpar={onLimpar ? limpar : undefined}
        />
      )}
    </>
  );
});
